import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';

type Site = {
  id: string; name: string; customer?: string;
  archived_at?: string | null;
  atgs: Array<{ id: string; label: string; archivedAt?: string | null }>;
};

const RANGE_PRESETS: Array<{ value: string; label: string; days: number }> = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
  { value: '12m', label: 'Last 12 months', days: 365 },
  { value: '24m', label: 'Last 24 months', days: 730 },
  { value: '36m', label: 'Last 36 months', days: 1095 },
  { value: 'custom', label: 'Custom range', days: 0 },
];

function isoDateOnly(d: Date) {
  return d.toISOString().slice(0, 10);
}

export function Reports() {
  const [sites, setSites] = useState<Site[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [tab, setTab] = useState<'deliveries' | 'sensors'>('deliveries');
  const [atgId, setAtgId] = useState<string>('');
  const [rangePreset, setRangePreset] = useState<string>('30d');
  const [from, setFrom] = useState<string>(isoDateOnly(new Date(Date.now() - 30 * 86400 * 1000)));
  const [to, setTo] = useState<string>(isoDateOnly(new Date()));
  const [tankFilter, setTankFilter] = useState<string>('');
  const [sensorFilter, setSensorFilter] = useState<string>('');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string>('');

  useEffect(() => {
    const qs = includeArchived ? '?includeArchived=1' : '';
    api.get(`/api/sites${qs}`).then((s: Site[]) => {
      setSites(s);
      // default to first ATG so the user sees data immediately
      const first = s.flatMap(x => x.atgs)[0];
      if (first && !atgId) setAtgId(first.id);
    }).catch(() => {});
  }, [includeArchived]);

  // When preset changes, update from/to (except 'custom' which leaves them alone)
  useEffect(() => {
    const preset = RANGE_PRESETS.find(r => r.value === rangePreset);
    if (!preset || preset.value === 'custom') return;
    const now = new Date();
    setFrom(isoDateOnly(new Date(now.getTime() - preset.days * 86400 * 1000)));
    setTo(isoDateOnly(now));
  }, [rangePreset]);

  const atgOptions = useMemo(() => sites.flatMap(s => s.atgs.map(a => ({
    id: a.id,
    label: `${s.name} — ${a.label}${(s.archived_at || a.archivedAt) ? ' (archived)' : ''}`,
  }))), [sites]);

  function buildQuery(extra: Record<string, string | undefined> = {}) {
    const params = new URLSearchParams();
    params.set('from', new Date(from + 'T00:00:00Z').toISOString());
    params.set('to', new Date(to + 'T23:59:59Z').toISOString());
    if (tab === 'deliveries' && tankFilter) params.set('tank', tankFilter);
    if (tab === 'sensors' && sensorFilter) params.set('sensor', sensorFilter);
    for (const [k, v] of Object.entries(extra)) if (v) params.set(k, v);
    return params.toString();
  }

  async function load() {
    if (!atgId) return;
    setLoading(true); setErr('');
    try {
      const path = tab === 'deliveries'
        ? `/api/atgs/${atgId}/deliveries?${buildQuery()}`
        : `/api/atgs/${atgId}/sensor-events?${buildQuery()}`;
      const data = await api.get(path);
      setRows(data);
    } catch (e: any) {
      setErr(e.message || 'Failed to load');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [atgId, tab, from, to, tankFilter, sensorFilter]);

  function csvUrl() {
    const path = tab === 'deliveries'
      ? `/api/atgs/${atgId}/deliveries?${buildQuery({ format: 'csv' })}`
      : `/api/atgs/${atgId}/sensor-events?${buildQuery({ format: 'csv' })}`;
    return path;
  }

  return (
    <>
      <div className="card">
        <h2>Reports</h2>
        <div className="row" style={{ alignItems: 'flex-end' }}>
          <div className="col">
            <label>Report</label>
            <select value={tab} onChange={e => setTab(e.target.value as any)}>
              <option value="deliveries">Delivery history</option>
              <option value="sensors">Sensor history</option>
            </select>
          </div>
          <div className="col">
            <label>Scope</label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', height: 32 }}>
              <input type="checkbox" checked={includeArchived} onChange={e => setIncludeArchived(e.target.checked)} />
              Include archived
            </label>
          </div>
          <div className="col">
            <label>ATG</label>
            <select value={atgId} onChange={e => setAtgId(e.target.value)}>
              {atgOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          <div className="col">
            <label>Range</label>
            <select value={rangePreset} onChange={e => setRangePreset(e.target.value)}>
              {RANGE_PRESETS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div className="col">
            <label>From</label>
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); setRangePreset('custom'); }} />
          </div>
          <div className="col">
            <label>To</label>
            <input type="date" value={to} onChange={e => { setTo(e.target.value); setRangePreset('custom'); }} />
          </div>
          {tab === 'deliveries' ? (
            <div className="col">
              <label>Tank (optional)</label>
              <input type="number" min={1} max={99} value={tankFilter} onChange={e => setTankFilter(e.target.value)} placeholder="all" />
            </div>
          ) : (
            <div className="col">
              <label>Sensor (optional)</label>
              <input type="number" min={1} max={99} value={sensorFilter} onChange={e => setSensorFilter(e.target.value)} placeholder="all" />
            </div>
          )}
          <div className="col" style={{ display: 'flex', gap: 8 }}>
            <a className="button" href={csvUrl()} download style={{ alignSelf: 'flex-end' }}>Download CSV</a>
          </div>
        </div>
        {err && <div style={{ color: 'crimson', marginTop: 8 }}>{err}</div>}
      </div>

      <div className="card">
        <h3>{tab === 'deliveries' ? 'Deliveries' : 'Sensor status changes'} — {rows.length} row{rows.length === 1 ? '' : 's'}</h3>
        {loading ? <div style={{ color: 'var(--muted)' }}>Loading…</div>
          : tab === 'deliveries' ? <DeliveryTable rows={rows} />
          : <SensorEventTable rows={rows} />}
      </div>
    </>
  );
}

function DeliveryTable({ rows }: { rows: any[] }) {
  if (!rows.length) return <div style={{ color: 'var(--muted)' }}>No deliveries in this range.</div>;
  return (
    <div className="table-wrap"><table>
      <thead>
        <tr>
          <th>Tank</th><th>Name</th><th>Started</th><th>Ended</th>
          <th>Amount (gal)</th><th>TC Amount</th>
          <th>Start vol</th><th>End vol</th>
          <th>Start ht</th><th>End ht</th>
          <th>Start °F</th><th>End °F</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r: any) => (
          <tr key={r.id}>
            <td>{r.tank_number}</td>
            <td>{r.tank_label || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
            <td>{new Date(r.started_at).toLocaleString()}</td>
            <td>{new Date(r.ended_at).toLocaleString()}</td>
            <td><b>{r.amount_volume?.toFixed?.(1)}</b></td>
            <td>{r.amount_tc_volume?.toFixed?.(1)}</td>
            <td>{r.start_volume?.toFixed?.(1)}</td>
            <td>{r.end_volume?.toFixed?.(1)}</td>
            <td>{r.start_height?.toFixed?.(2)}</td>
            <td>{r.end_height?.toFixed?.(2)}</td>
            <td>{r.start_temp?.toFixed?.(1)}</td>
            <td>{r.end_temp?.toFixed?.(1)}</td>
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}

function SensorEventTable({ rows }: { rows: any[] }) {
  if (!rows.length) return <div style={{ color: 'var(--muted)' }}>No sensor status changes in this range.</div>;
  return (
    <div className="table-wrap"><table>
      <thead>
        <tr><th>When</th><th>Sensor</th><th>Location</th><th>From</th><th>To</th></tr>
      </thead>
      <tbody>
        {rows.map((r: any, i: number) => (
          <tr key={i}>
            <td>{new Date(r.collected_at).toLocaleString()}</td>
            <td>{r.sensor_number}</td>
            <td>{r.sensor_label || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
            <td>{r.prev_status_label || <span style={{ color: 'var(--muted)' }}>(initial)</span>}</td>
            <td>
              <span className={`pill ${r.status_code === 0 ? 'ok' : 'err'}`}>
                {r.status_label}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}
